import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './FilePreview.css';
import './Services.css';
import PropTypes from 'prop-types';
import LoadingSpinner from '../components/LoadingSpinner';

const API_BASE = process.env.REACT_APP_FILE_API_URL;

const FilePreview = () => {
  const { user } = useAuth0();
  const username = user?.nickname || user?.name || user?.email;
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('preview'); // 'preview' or 'grid'

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/files/user/${username}`, {
          headers: {
            'Authorization': 'Basic YWRtaW46YWRtaW4='
          }
        });
        if (!res.ok) throw new Error('Failed to fetch files');
        setFiles(await res.json());
      } catch (error) {
        setError(`Could not load files: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    if (username) fetchFiles();
  }, [username]);

  return (
    <div className="service-page">
      <div className="file-preview-container">
        <h1>Preview Files</h1>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <button
            onClick={() => setViewMode('preview')}
            className={viewMode === 'preview' ? 'file-preview-link' : ''}
            style={{ minWidth: 120 }}
          >
            Preview View
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={viewMode === 'grid' ? 'file-preview-link' : ''}
            style={{ minWidth: 120 }}
          >
            Grid View
          </button>
        </div>
        {loading && <div className="loading-overlay"><LoadingSpinner /></div>}
        {error && <div className="file-error">{error}</div>}
        <div className="file-list">
          {files.length === 0 && (
            <div className="profile-details">
              <p>No files uploaded yet. 📁</p>
              <p className="text-gray-500">Upload some files to see them here!</p>
            </div>
          )}
          {viewMode === 'grid' ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.2rem' }}>
              {files.map((file) => (
                <GridFileCard key={file.id} file={file} />
              ))}
            </div>
          ) : (
            files.map((file) => (
              <FilePreviewCard key={file.id} file={file} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const FilePreviewCard = ({ file }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [playError, setPlayError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const API_BASE = process.env.REACT_APP_FILE_API_URL;
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setShowPreview((v) => !v);
    }
  };
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this file?')) return;
    setDeleting(true);
    setError('');
    try {
      await fetch(`${API_BASE}/files/user/${file.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic YWRtaW46YWRtaW4='
        }
      });
      window.location.reload();
    } catch (error) {
      setError(`Delete failed: ${error.message}`);
    }
    setDeleting(false);
  };
  const handleDownload = async (e) => {
    e.preventDefault();
    setDownloading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/files/download/${file.id}`, {
        headers: {
          'Authorization': 'Basic YWRtaW46YWRtaW4='
        }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Download failed.');
    }
    setDownloading(false);
  };
  return (
    <div className="file-card file-preview-beautiful">
      <div
        className="file-info file-info-preview"
        onClick={() => setShowPreview((v) => !v)}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-pressed={showPreview}
        title={showPreview ? 'Hide preview' : 'Show preview'}
      >
        <div className="file-info-details">
          <span className="file-name file-name-large">{file.fileName}</span>
          <div>
            <span className="file-type file-type-pill">{file.contentType}</span>
            {file.size && (
              <span className="file-size file-size-pill">
                {' · '}{(file.size / 1024).toFixed(2)} KB
              </span>
            )}
          </div>
          <span className="file-date file-date-small">
            Uploaded on {new Date(file.uploadDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>
        <div className="preview-toggle">
          {showPreview ? '👁️ Hide Preview' : '👁️ Show Preview'}
        </div>
      </div>
      {showPreview && (
        <div className="file-preview-area file-preview-area-beautiful">
          {file.contentType.startsWith('image/') && (
            <div className="file-preview-media file-preview-media-image">
              <img 
                src={`${API_BASE}/files/download/${file.id}`} 
                alt={file.fileName} 
                className="image-preview file-preview-media"
                style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 12, boxShadow: '0 4px 24px rgba(99,102,241,0.10)' }}
              />
            </div>
          )}
          {file.contentType.startsWith('video/') && (
            <div className="video-preview-container file-preview-media-video">
              <video 
                src={`${API_BASE}/files/download/${file.id}`}
                className="video-preview file-preview-media"
                controls
                style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 12, boxShadow: '0 4px 24px rgba(99,102,241,0.10)' }}
                onMouseEnter={async (e) => {
                  setPlayError('');
                  try {
                    await e.target.play();
                  } catch (err) {
                    setPlayError('Video playback is not supported in this browser or requires user interaction.');
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.pause();
                  e.target.currentTime = 0;
                }}
                muted
              >
                <track kind="captions" />
              </video>
              {playError && <div className="file-error">{playError}</div>}
            </div>
          )}
          {file.contentType === 'application/pdf' && (
            <a 
              href={`${API_BASE}/files/download/${file.id}`} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="file-preview-link"
            >
              📄 Preview PDF
            </a>
          )}
          <div className="file-actions file-actions-preview">
            <button 
              className="file-preview-link file-download-btn" 
              onClick={handleDownload} 
              disabled={downloading}
              style={{ minWidth: 120 }}
            >
              {downloading ? 'Downloading...' : '⬇️ Download'}
            </button>
            <button 
              className="file-delete-btn" 
              onClick={handleDelete} 
              disabled={deleting}
              style={{ minWidth: 120 }}
            >
              {deleting ? <><LoadingSpinner /> Deleting...</> : '🗑️ Delete'}
            </button>
          </div>
          {error && <div className="file-error">{error}</div>}
        </div>
      )}
    </div>
  );
};

// Simple grid card for grid view
const GridFileCard = ({ file }) => {
  const API_BASE = process.env.REACT_APP_FILE_API_URL;
  return (
    <div className="file-card" style={{ cursor: 'default' }}>
      <div className="file-info-details">
        <span className="file-name">{file.fileName}</span>
        <div>
          <span className="file-type">{file.contentType}</span>
          {file.size && (
            <span className="file-size">
              {' · '}{(file.size / 1024).toFixed(2)} KB
            </span>
          )}
        </div>
        <span className="file-date">
          Uploaded on {new Date(file.uploadDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      </div>
      <div className="file-actions">
        <a 
          href={`${API_BASE}/files/download/${file.id}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="file-preview-link"
        >
          ⬇️ Download
        </a>
      </div>
    </div>
  );
};

FilePreviewCard.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    fileName: PropTypes.string.isRequired,
    size: PropTypes.number,
    contentType: PropTypes.string.isRequired,
    uploadDate: PropTypes.string.isRequired,
  }).isRequired,
};

GridFileCard.propTypes = FilePreviewCard.propTypes;

export default FilePreview;
